import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import '@/App.css';

// Eager — needed before any lazy chunk can resolve, or part of the app shell.
import LoginPage from '@/pages/LoginPage';
import AuthCallback from '@/pages/AuthCallback';
import ForgotPassword from '@/pages/ForgotPassword';
import ChangePassword from '@/pages/ChangePassword';
import ProtectedRoute from '@/components/ProtectedRoute';
import Layout from '@/components/Layout';
import { Toaster } from '@/components/ui/sonner';

// Lazy — every authenticated route is split into its own chunk so the
// initial bundle stays small. Webpack/CRA will produce one .chunk.js per
// import() below, fetched on demand when the user navigates.
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Customers = lazy(() => import('@/pages/Customers'));
const Employees = lazy(() => import('@/pages/Employees'));
const Salaries = lazy(() => import('@/pages/Salaries'));
const Revenues = lazy(() => import('@/pages/Revenues'));
const Allocations = lazy(() => import('@/pages/Allocations'));
const ManpowerReport = lazy(() => import('@/pages/ManpowerReport'));
const ClientReport = lazy(() => import('@/pages/ClientReport'));
const UnallocatedReport = lazy(() => import('@/pages/UnallocatedReport'));
const ManagersReport = lazy(() => import('@/pages/ManagersReport'));
const CostBreakdownReport = lazy(() => import('@/pages/CostBreakdownReport'));
const ProductReport = lazy(() => import('@/pages/ProductReport'));
const BulkAllocationEditor = lazy(() => import('@/pages/BulkAllocationEditor'));
const BulkSalaryEditor = lazy(() => import('@/pages/BulkSalaryEditor'));
const BulkRevenueEditor = lazy(() => import('@/pages/BulkRevenueEditor'));
const UserMaster = lazy(() => import('@/pages/UserMaster'));
const RoleMaster = lazy(() => import('@/pages/RoleMaster'));
const VerticalMaster = lazy(() => import('@/pages/VerticalMaster'));
const ProductMaster = lazy(() => import('@/pages/ProductMaster'));
const CloudMaster = lazy(() => import('@/pages/CloudMaster'));
const ServerTypeMaster = lazy(() => import('@/pages/ServerTypeMaster'));
const ServerMaster = lazy(() => import('@/pages/ServerMaster'));
const BulkServerCostEditor = lazy(() => import('@/pages/BulkServerCostEditor'));
const ServerAllocations = lazy(() => import('@/pages/ServerAllocations'));
const CloudRevenues = lazy(() => import('@/pages/CloudRevenues'));
const CloudClientReport = lazy(() => import('@/pages/CloudClientReport'));
const CloudProductReport = lazy(() => import('@/pages/CloudProductReport'));
const CloudServerReport = lazy(() => import('@/pages/CloudServerReport'));
const UnallocatedServerReport = lazy(() => import('@/pages/UnallocatedServerReport'));
const CloudDashboard = lazy(() => import('@/pages/CloudDashboard'));
const AuditLogs = lazy(() => import('@/pages/AuditLogs'));
const Maintenance = lazy(() => import('@/pages/Maintenance'));
const DataHygiene = lazy(() => import('@/pages/DataHygiene'));
const Integrations = lazy(() => import('@/pages/Integrations'));
const CompanySettings = lazy(() => import('@/pages/CompanySettings'));
const SystemOverview = lazy(() => import('@/pages/SystemOverview'));
const CockpitActivity = lazy(() => import('@/pages/CockpitActivity'));
const MeetingIntelligence = lazy(() => import('@/pages/MeetingIntelligence'));
const NotificationSettings = lazy(() => import('@/pages/NotificationSettings'));
const ModuleUtilization = lazy(() => import('@/pages/ModuleUtilization'));
const ModuleUtilizationReport = lazy(() => import('@/pages/ModuleUtilizationReport'));
const ClientDashboard = lazy(() => import('@/pages/ClientDashboard'));
const ProjectMaster = lazy(() => import('@/pages/ProjectMaster'));
const ContactMaster = lazy(() => import('@/pages/ContactMaster'));
const ClientMeetings = lazy(() => import('@/pages/ClientMeetings'));
const ClientTrainings = lazy(() => import('@/pages/ClientTrainings'));
const ClientProfileDashboard = lazy(() => import('@/pages/ClientProfileDashboard'));
const TaskTypeMaster = lazy(() => import('@/pages/TaskTypeMaster'));
const TaskStatusMaster = lazy(() => import('@/pages/TaskStatusMaster'));
const ProjectPlan = lazy(() => import('@/pages/ProjectPlan'));
const UoMMaster = lazy(() => import('@/pages/UoMMaster'));
const ItemGroupMaster = lazy(() => import('@/pages/ItemGroupMaster'));
const ItemsMaster = lazy(() => import('@/pages/ItemsMaster'));
const Proposals = lazy(() => import('@/pages/Proposals'));
const Contracts = lazy(() => import('@/pages/Contracts'));
const PurchaseOrders = lazy(() => import('@/pages/PurchaseOrders'));
const CurrencyMaster = lazy(() => import('@/pages/CurrencyMaster'));
const Invoices = lazy(() => import('@/pages/Invoices'));
const PricingRules = lazy(() => import('@/pages/PricingRules'));
const CommercialDashboard = lazy(() => import('@/pages/CommercialDashboard'));
const PaymentSplitMaster = lazy(() => import('@/pages/PaymentSplitMaster'));
const ContactCoverageDashboard = lazy(() => import('@/pages/ContactCoverageDashboard'));
const ProjectReviews = lazy(() => import('@/pages/ProjectReviews'));
const ReviewDashboard = lazy(() => import('@/pages/ReviewDashboard'));
const ServerClusters = lazy(() => import('@/pages/ServerClusters'));
const AdvancedReports = lazy(() => import('@/pages/AdvancedReports'));
const ReportRevenue = lazy(() => import('@/pages/reports/ReportRevenue'));
const ReportWinRate = lazy(() => import('@/pages/reports/ReportWinRate'));
const ReportInvoiceAging = lazy(() => import('@/pages/reports/ReportInvoiceAging'));
const ReportPOUtilization = lazy(() => import('@/pages/reports/ReportPOUtilization'));
const ReportExpiryCalendar = lazy(() => import('@/pages/reports/ReportExpiryCalendar'));
const ReportProfitability = lazy(() => import('@/pages/reports/ReportProfitability'));
const ReportEmployeeUtilization = lazy(() => import('@/pages/reports/ReportEmployeeUtilization'));
const ReportProjectHealth = lazy(() => import('@/pages/reports/ReportProjectHealth'));
const ReportContactCoverage = lazy(() => import('@/pages/reports/ReportContactCoverage'));
const ReportContactList = lazy(() => import('@/pages/reports/ReportContactList'));
const ReportClientEngagement = lazy(() => import('@/pages/reports/ReportClientEngagement'));
const ReportServerCost = lazy(() => import('@/pages/reports/ReportServerCost'));
const ReportCloudSpend = lazy(() => import('@/pages/reports/ReportCloudSpend'));
const ManageManpowerAllocations = lazy(() => import('@/pages/ManageManpowerAllocations'));
const ManageManpowerRevenue = lazy(() => import('@/pages/ManageManpowerRevenue'));
const ManageCloudAllocations = lazy(() => import('@/pages/ManageCloudAllocations'));
const ManageCloudRevenue = lazy(() => import('@/pages/ManageCloudRevenue'));
const ReportManpowerAllocationHistory = lazy(() => import('@/pages/reports/ReportManpowerAllocationHistory'));
const ReportManpowerRevenueHistory = lazy(() => import('@/pages/reports/ReportManpowerRevenueHistory'));
const ReportCloudAllocationHistory = lazy(() => import('@/pages/reports/ReportCloudAllocationHistory'));
const ReportCloudRevenueHistory = lazy(() => import('@/pages/reports/ReportCloudRevenueHistory'));

// Map of route href → chunk loader. Layout.js uses this to prefetch a route's
// chunk on sidebar hover. Webpack dedupes dynamic imports by their module
// specifier (the string inside `import(...)`), so calling the loader on hover
// kicks the chunk fetch; React.lazy then resolves from the same cache when
// the user actually clicks.
export const ROUTE_PREFETCHERS = {
  '/dashboard': () => import('@/pages/Dashboard'),
  '/customers': () => import('@/pages/Customers'),
  '/contacts': () => import('@/pages/ContactMaster'),
  '/verticals': () => import('@/pages/VerticalMaster'),
  '/products': () => import('@/pages/ProductMaster'),
  '/employees': () => import('@/pages/Employees'),
  '/salaries': () => import('@/pages/Salaries'),
  '/salaries/bulk-edit': () => import('@/pages/BulkSalaryEditor'),
  '/revenues': () => import('@/pages/Revenues'),
  '/revenues/bulk-edit': () => import('@/pages/BulkRevenueEditor'),
  '/allocations': () => import('@/pages/Allocations'),
  '/allocations/bulk-edit': () => import('@/pages/BulkAllocationEditor'),
  '/cloud/clouds': () => import('@/pages/CloudMaster'),
  '/cloud/server-types': () => import('@/pages/ServerTypeMaster'),
  '/cloud/servers': () => import('@/pages/ServerMaster'),
  '/cloud/server-clusters': () => import('@/pages/ServerClusters'),
  '/cloud/server-costs': () => import('@/pages/BulkServerCostEditor'),
  '/cloud/server-allocations': () => import('@/pages/ServerAllocations'),
  '/cloud/revenues': () => import('@/pages/CloudRevenues'),
  '/cloud/dashboard': () => import('@/pages/CloudDashboard'),
  '/cloud/reports/client': () => import('@/pages/CloudClientReport'),
  '/cloud/reports/product': () => import('@/pages/CloudProductReport'),
  '/cloud/reports/server': () => import('@/pages/CloudServerReport'),
  '/cloud/reports/unallocated': () => import('@/pages/UnallocatedServerReport'),
  '/reports/manpower': () => import('@/pages/ManpowerReport'),
  '/reports/client': () => import('@/pages/ClientReport'),
  '/reports/unallocated': () => import('@/pages/UnallocatedReport'),
  '/reports/managers': () => import('@/pages/ManagersReport'),
  '/reports/cost-breakdown': () => import('@/pages/CostBreakdownReport'),
  '/reports/product': () => import('@/pages/ProductReport'),
  '/project/module-utilization': () => import('@/pages/ModuleUtilization'),
  '/project/module-utilization-report': () => import('@/pages/ModuleUtilizationReport'),
  '/project/client-dashboard': () => import('@/pages/ClientDashboard'),
  '/project/projects': () => import('@/pages/ProjectMaster'),
  '/project/meetings': () => import('@/pages/ClientMeetings'),
  '/project/trainings': () => import('@/pages/ClientTrainings'),
  '/project/client-profile': () => import('@/pages/ClientProfileDashboard'),
  '/project/task-types': () => import('@/pages/TaskTypeMaster'),
  '/project/task-statuses': () => import('@/pages/TaskStatusMaster'),
  '/project/plan': () => import('@/pages/ProjectPlan'),
  '/project/contact-coverage': () => import('@/pages/ContactCoverageDashboard'),
  '/project/reviews': () => import('@/pages/ProjectReviews'),
  '/project/review-dashboard': () => import('@/pages/ReviewDashboard'),
  '/project/meeting-intelligence': () => import('@/pages/MeetingIntelligence'),
  '/masters/uom': () => import('@/pages/UoMMaster'),
  '/masters/item-groups': () => import('@/pages/ItemGroupMaster'),
  '/masters/items': () => import('@/pages/ItemsMaster'),
  '/masters/currencies': () => import('@/pages/CurrencyMaster'),
  '/masters/payment-splits': () => import('@/pages/PaymentSplitMaster'),
  '/commercial/proposals': () => import('@/pages/Proposals'),
  '/commercial/contracts': () => import('@/pages/Contracts'),
  '/commercial/purchase-orders': () => import('@/pages/PurchaseOrders'),
  '/commercial/invoices': () => import('@/pages/Invoices'),
  '/commercial/pricing-rules': () => import('@/pages/PricingRules'),
  '/commercial/dashboard': () => import('@/pages/CommercialDashboard'),
  '/commercial/reports': () => import('@/pages/AdvancedReports'),
  '/reports/revenue': () => import('@/pages/reports/ReportRevenue'),
  '/reports/win-rate': () => import('@/pages/reports/ReportWinRate'),
  '/reports/invoice-aging': () => import('@/pages/reports/ReportInvoiceAging'),
  '/reports/po-utilization': () => import('@/pages/reports/ReportPOUtilization'),
  '/reports/expiry-calendar': () => import('@/pages/reports/ReportExpiryCalendar'),
  '/reports/profitability': () => import('@/pages/reports/ReportProfitability'),
  '/reports/employee-utilization': () => import('@/pages/reports/ReportEmployeeUtilization'),
  '/reports/project-health': () => import('@/pages/reports/ReportProjectHealth'),
  '/reports/contact-coverage': () => import('@/pages/reports/ReportContactCoverage'),
  '/reports/client-engagement': () => import('@/pages/reports/ReportClientEngagement'),
  '/reports/contact-list': () => import('@/pages/reports/ReportContactList'),
  '/reports/server-cost': () => import('@/pages/reports/ReportServerCost'),
  '/reports/cloud-spend': () => import('@/pages/reports/ReportCloudSpend'),
  '/allocations/manage': () => import('@/pages/ManageManpowerAllocations'),
  '/revenues/manage': () => import('@/pages/ManageManpowerRevenue'),
  '/cloud/allocations/manage': () => import('@/pages/ManageCloudAllocations'),
  '/cloud/revenues/manage': () => import('@/pages/ManageCloudRevenue'),
  '/reports/manpower-allocation-history': () => import('@/pages/reports/ReportManpowerAllocationHistory'),
  '/reports/manpower-revenue-history': () => import('@/pages/reports/ReportManpowerRevenueHistory'),
  '/cloud/reports/allocation-history': () => import('@/pages/reports/ReportCloudAllocationHistory'),
  '/cloud/reports/revenue-history': () => import('@/pages/reports/ReportCloudRevenueHistory'),
  '/users': () => import('@/pages/UserMaster'),
  '/roles': () => import('@/pages/RoleMaster'),
  '/integrations': () => import('@/pages/Integrations'),
  '/company-settings': () => import('@/pages/CompanySettings'),
  '/dashboards/overview': () => import('@/pages/SystemOverview'),
  '/dashboards/activity': () => import('@/pages/CockpitActivity'),
  '/notification-settings': () => import('@/pages/NotificationSettings'),
  '/audit-logs': () => import('@/pages/AuditLogs'),
  '/maintenance': () => import('@/pages/Maintenance'),
  '/data-hygiene': () => import('@/pages/DataHygiene'),
};

// Suspense fallback used while a route chunk is being downloaded.
const RouteLoader = () => (
  <div className="flex items-center justify-center min-h-screen" data-testid="route-loader">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check for session_id synchronously during render (not in useEffect)
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route
          path="/change-password"
          element={<ProtectedRoute><ChangePassword /></ProtectedRoute>}
        />

        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/contacts" element={<ContactMaster />} />
          <Route path="/verticals" element={<VerticalMaster />} />
          <Route path="/products" element={<ProductMaster />} />
          <Route path="/employees" element={<Employees />} />
          <Route path="/salaries" element={<Salaries />} />
          <Route path="/salaries/bulk-edit" element={<BulkSalaryEditor />} />
          <Route path="/revenues" element={<Revenues />} />
          <Route path="/revenues/bulk-edit" element={<BulkRevenueEditor />} />
          <Route path="/allocations" element={<Allocations />} />
          <Route path="/allocations/bulk-edit" element={<BulkAllocationEditor />} />
          <Route path="/cloud/clouds" element={<CloudMaster />} />
          <Route path="/cloud/server-types" element={<ServerTypeMaster />} />
          <Route path="/cloud/servers" element={<ServerMaster />} />
          <Route path="/cloud/server-clusters" element={<ServerClusters />} />
          <Route path="/cloud/server-costs" element={<BulkServerCostEditor />} />
          <Route path="/cloud/server-allocations" element={<ServerAllocations />} />
          <Route path="/cloud/revenues" element={<CloudRevenues />} />
          <Route path="/cloud/dashboard" element={<CloudDashboard />} />
          <Route path="/cloud/reports/client" element={<CloudClientReport />} />
          <Route path="/cloud/reports/product" element={<CloudProductReport />} />
          <Route path="/cloud/reports/server" element={<CloudServerReport />} />
          <Route path="/cloud/reports/unallocated" element={<UnallocatedServerReport />} />
          <Route path="/reports/manpower" element={<ManpowerReport />} />
          <Route path="/reports/client" element={<ClientReport />} />
          <Route path="/reports/unallocated" element={<UnallocatedReport />} />
          <Route path="/reports/managers" element={<ManagersReport />} />
          <Route path="/reports/cost-breakdown" element={<CostBreakdownReport />} />
          <Route path="/reports/product" element={<ProductReport />} />
          <Route path="/project/module-utilization" element={<ModuleUtilization />} />
          <Route path="/project/module-utilization-report" element={<ModuleUtilizationReport />} />
          <Route path="/project/client-dashboard" element={<ClientDashboard />} />
          <Route path="/project/projects" element={<ProjectMaster />} />
          <Route path="/project/meetings" element={<ClientMeetings />} />
          <Route path="/project/trainings" element={<ClientTrainings />} />
          <Route path="/project/client-profile" element={<ClientProfileDashboard />} />
          <Route path="/project/task-types" element={<TaskTypeMaster />} />
          <Route path="/project/task-statuses" element={<TaskStatusMaster />} />
          <Route path="/project/plan" element={<ProjectPlan />} />
          <Route path="/masters/uom" element={<UoMMaster />} />
          <Route path="/masters/item-groups" element={<ItemGroupMaster />} />
          <Route path="/masters/items" element={<ItemsMaster />} />
          <Route path="/commercial/proposals" element={<Proposals />} />
          <Route path="/commercial/contracts" element={<Contracts />} />
          <Route path="/commercial/purchase-orders" element={<PurchaseOrders />} />
          <Route path="/commercial/invoices" element={<Invoices />} />
          <Route path="/commercial/pricing-rules" element={<PricingRules />} />
          <Route path="/commercial/dashboard" element={<CommercialDashboard />} />
          <Route path="/masters/currencies" element={<CurrencyMaster />} />
          <Route path="/masters/payment-splits" element={<PaymentSplitMaster />} />
          <Route path="/project/contact-coverage" element={<ContactCoverageDashboard />} />
          <Route path="/project/reviews" element={<ProjectReviews />} />
          <Route path="/project/review-dashboard" element={<ReviewDashboard />} />
          <Route path="/commercial/reports" element={<AdvancedReports />} />
          <Route path="/reports/revenue" element={<ReportRevenue />} />
          <Route path="/reports/win-rate" element={<ReportWinRate />} />
          <Route path="/reports/invoice-aging" element={<ReportInvoiceAging />} />
          <Route path="/reports/po-utilization" element={<ReportPOUtilization />} />
          <Route path="/reports/expiry-calendar" element={<ReportExpiryCalendar />} />
          <Route path="/reports/profitability" element={<ReportProfitability />} />
          <Route path="/reports/employee-utilization" element={<ReportEmployeeUtilization />} />
          <Route path="/reports/project-health" element={<ReportProjectHealth />} />
          <Route path="/reports/contact-coverage" element={<ReportContactCoverage />} />
          <Route path="/reports/client-engagement" element={<ReportClientEngagement />} />
          <Route path="/reports/contact-list" element={<ReportContactList />} />
          <Route path="/reports/server-cost" element={<ReportServerCost />} />
          <Route path="/reports/cloud-spend" element={<ReportCloudSpend />} />
          <Route path="/allocations/manage" element={<ManageManpowerAllocations />} />
          <Route path="/revenues/manage" element={<ManageManpowerRevenue />} />
          <Route path="/cloud/allocations/manage" element={<ManageCloudAllocations />} />
          <Route path="/cloud/revenues/manage" element={<ManageCloudRevenue />} />
          <Route path="/reports/manpower-allocation-history" element={<ReportManpowerAllocationHistory />} />
          <Route path="/reports/manpower-revenue-history" element={<ReportManpowerRevenueHistory />} />
          <Route path="/cloud/reports/allocation-history" element={<ReportCloudAllocationHistory />} />
          <Route path="/cloud/reports/revenue-history" element={<ReportCloudRevenueHistory />} />
          <Route path="/users" element={<UserMaster />} />
          <Route path="/roles" element={<RoleMaster />} />
          <Route path="/email-config" element={<Navigate to="/integrations?tab=email" replace />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/company-settings" element={<CompanySettings />} />
          <Route path="/dashboards/overview" element={<SystemOverview />} />
          <Route path="/dashboards/activity" element={<CockpitActivity />} />
          <Route path="/project/meeting-intelligence" element={<MeetingIntelligence />} />
          <Route path="/notification-settings" element={<NotificationSettings />} />
          <Route path="/audit-logs" element={<AuditLogs />} />
          <Route path="/maintenance" element={<Maintenance />} />
          <Route path="/data-hygiene" element={<DataHygiene />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
